import { useState, MouseEvent } from 'react'
import ExcelJS from 'exceljs'
import { numberFormat } from 'shared/util'

import { useSearch } from 'features/search/context'
import { usePrint } from 'routes/context/printContext'
import { SisetuResult } from 'interface/sisetu/types'

import { SisetuListRecord } from './record'

import {
  Box,
  Alert,
  Stack,
  Button,
  Typography,
  StackProps,
} from '@mui/material'

import { Virtuoso } from 'react-virtuoso'

import { DownloadIcon } from 'shared/icons'

import {
  SisetuListDownloadDialog,
  FormatTypes,
  ContentTypes,
} from './downloadDialog'

import { ReportFormatList } from '../report/list'

type SisetuListProps = {
  loading: boolean
  sisetuList: SisetuResult[]
  selectedSisetuCode?: string | null
  onSelectSisetu?: (sisetuCode: string) => void
} & StackProps
export const SisetuList = ({
  loading,
  sisetuList,
  selectedSisetuCode,
  onSelectSisetu,
  ...others
}: SisetuListProps) => {
  const search = useSearch()
  const print = usePrint()

  const [openDownloadDialog, setOpenDownloadDialog] = useState<{
    sisetuCount: number
  } | null>(null)

  const onDownload = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setOpenDownloadDialog({ sisetuCount: sisetuList.length })
  }
  const doDownload = async (
    format: FormatTypes,
    content: ContentTypes,
    reportId: string | null,
    from: number,
    to: number
  ): Promise<boolean> => {
    const targetList = sisetuList.slice(from, to)
    if (targetList.length === 0) return false

    if (format === 'report') {
      if (!reportId || !print || !search) return false

      const formatter = ReportFormatList.find((r) => r.reportId === reportId)
      if (!formatter) return false

      print.doPrint(
        <Stack direction="column" spacing={1}>
          {targetList.map((sisetu, i) => (
            <formatter.record
              key={`print-record-${i}`}
              search={search}
              sisetu={sisetu}
              counter={i}
              count={targetList.length}
            />
          ))}
        </Stack>
      )
    } else {
      const workbook = new ExcelJS.Workbook()
      workbook.addWorksheet('sheet1')
      const worksheet = workbook.getWorksheet('sheet1')
      if (!worksheet) return false

      if (content === 'name') {
        worksheet.columns = [
          { header: 'DCF', key: 'sisetuCode' },
          { header: 'NAME', key: 'sisetuName' },
        ]
      } else if (content === 'all') {
        worksheet.columns = [
          { header: 'DCF', key: 'sisetuCode' },
          { header: 'NAME', key: 'sisetuName' },
          { header: 'KANA', key: 'sisetuNameKana' },
          { header: 'ADDR', key: 'juusyo' },
          { header: 'TEL', key: 'tel' },
          { header: 'CODE', key: 'institutionalCode' },
        ]
      } else {
        worksheet.columns = [{ header: 'DCF', key: 'sisetuCode' }]
      }
      worksheet.addRows(
        targetList.map((sisetu) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const row: { [index: string]: any } = {}
          worksheet.columns.forEach((column) => {
            if (!column.key) return
            row[column.key] = (
              column.key === 'sisetuCode' ? sisetu.sisetuCode :
              column.key === 'sisetuName' ? sisetu.sisetuName :
              column.key === 'sisetuNameKana' ? sisetu.sisetuNameKana :
              column.key === 'juusyo' ? sisetu.juusyo :
              column.key === 'tel' ? sisetu.tel:
              column.key === 'institutionalCode' ? sisetu.institutionalCode : undefined
            )
          })
          return row
        })
      )

      const uint8Array = format === 'csv' ? (
        await workbook.csv.writeBuffer()
      ) : format === 'tsv' ? (
        await workbook.csv.writeBuffer({
          formatterOptions: {
            delimiter: '\t',
            quote: false,
          }
        })
      ) : (
        await workbook.xlsx.writeBuffer()
      )
      const blob = new Blob([uint8Array], { type: 'application/octet-binary' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download =
        'sisetuList' +
        (format === 'csv' ? '.csv' : format === 'tsv' ? '.tsv' : '.xlsx')
      a.click()
      a.remove()
    }
    return true
  }

  return (
    <Stack direction="column" overflow="hidden" position="relative" {...others}>
      <Box flexGrow={1}>
        {sisetuList.length === 0 ? (
          loading === false && (
            <Box padding={1}>
              <Alert severity="info">対象が見つかりません.</Alert>
            </Box>
          )
        ) : (
          <Virtuoso
            style={{
              height: '100%',
            }}
            data={sisetuList}
            itemContent={(index, sisetu) => {
              return (
                <SisetuListRecord
                  key={sisetu.sisetuCode}
                  sisetu={sisetu}
                  rank={index + 1}
                  selected={sisetu.sisetuCode === selectedSisetuCode}
                  onSelectSisetu={onSelectSisetu}
                />
              )
            }}
          />
        )}
      </Box>

      <Stack
        direction="row"
        padding={1}
        spacing={1}
        sx={{ borderTop: 1, borderColor: 'divider' }}
        justifyContent="flex-start"
        alignItems="center"
      >
        <Typography variant="button" component="div" flexGrow={1} paddingX={2}>
          {numberFormat(sisetuList?.length, '-')} 施設
        </Typography>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          sx={{ whiteSpace: 'nowrap' }}
          disabled={!sisetuList || sisetuList.length === 0}
          onClick={onDownload}
        >
          ダウンロード
        </Button>
      </Stack>

      {openDownloadDialog && (
        <SisetuListDownloadDialog
          sisetuCount={openDownloadDialog.sisetuCount}
          onDownload={async (format, content, reportId, from, to) => {
            if (await doDownload(format, content, reportId, from, to)) {
              setOpenDownloadDialog(null)
            }
          }}
          onClose={() => setOpenDownloadDialog(null)}
        />
      )}
    </Stack>
  )
}
